// src/Services/LearnerService/LearningsService.ts
import httpClient from '@/services/httpClient';
import { transformBackendCourse, transformBackendCourseDetails, BackendCourse, BackendCourseDetails } from '@/utils/courseMapper';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Standard API response wrapper from backend
 * All API endpoints return this structure: { success, data, message }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

// Course interface - used across all pages
export interface Course {
  id: number;
  title: string;
  description: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  price: string;
  image: string;
  imageClass?: string;
  authorLabel: string;
  authorLocation: string;
  displayMode: 'my-courses' | 'browse' | 'wishlist';

  // Enrollment status from backend
  is_enrolled?: boolean;
  enrolled_date?: string;

  // For My Courses (with progress)
  completedCount?: number;
  totalCount?: number;
  durationMinutes?: number;
  dueDate?: string;
  progressFilled?: number;
  progressText?: string;
  isCompleted?: boolean;

  // For Browse & Wishlist (without progress)
  totalModules?: number;
  totalHours?: number;
}

// Lesson interface - represents a single lesson in a course
export interface Lesson {
  id: number;
  title: string;
  duration: string;
  lessonType: 'Content-Based' | 'ILTS';
  isCompleted: boolean;
  isUnlocked: boolean;

  // Content-Based lesson properties
  contentType?: 'document' | 'mp4' | 'scorm' | 'url';
  contentUrl?: string;

  // ILTS lesson properties
  iltsMode?: 'Online' | 'Offline';
  iltsInfo?: {
    mode: string;
    meet_url?: string;
    venue?: string;
    start_date?: string;
    start_time?: string;
    end_date?: string;
    end_time?: string;
    message?: string;
  };
}

// Module interface - represents a section/module in a course
export interface CourseModule {
  id: string;
  title: string;
  lectures: number;
  duration: string;
  lessons: Lesson[];
}

export interface FAQ {
  id: number;
  question: string;
  answer: string;
}

export interface Review {
  id: number;
  name: string;
  avatar: string;
  rating: number;
  time: string;
  comment: string;
}

export interface Instructor {
  name: string;
  title: string;
  avatar: string;
  rating: number;
  totalRatings: number;
  totalStudents: number;
  totalCourses: number;
  bio: string[];
}

export interface CourseDetails {
  id: number;
  title: string;
  subtitle: string;
  rating: number;
  totalRatings: number;
  totalStudents: number;
  instructor: Instructor;
  courseIncludes: string[];
  learningPoints: string[];
  courseModules: CourseModule[];
  lessons: Lesson[];
  reviews: Review[];
  faqs: FAQ[];
  contentStats: {
    sections: number;
    lectures: number;
    totalDuration: string;
  };
  is_enrolled?: boolean;
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const ENDPOINTS = {
  MY_COURSES: '/student/courses/subscribed',
  WISHLIST: '/student/courses/saved',
  BROWSE_COURSES: '/student/courses/explore',
  COURSE_DETAILS: (courseId: number) => `/student/courses/${courseId}`,
  SAVE_COURSE: '/student/courses/save',
  UNSAVE_COURSE: '/student/courses/unsave',
  ENROLL: '/student/courses/enroll',
  MARK_LESSON_COMPLETE: (courseId: number, lessonId: number) => `/student/courses/${courseId}/lessons/${lessonId}/complete`,
};

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Fetch My Courses
 * Returns courses the user is enrolled in with progress tracking
 *
 * MIGRATION GUIDE:
 * 1. Uncomment import: import httpClient from '@/services/httpClient';
 * 2. Comment out the MOCK DATA block below
 * 3. Uncomment the API IMPLEMENTATION block
 * 4. Test the endpoint
 */
export const getMyCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    // Correct: Expect ApiResponse wrapper
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.MY_COURSES);

    // Access the data property which contains the actual array
    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    // Map over response.data, not response
    const courses = response.data.map(course =>
      transformBackendCourse(course, 'my-courses')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch my courses' }
    };
  }
};

/**
 * Fetch Wishlist Courses
 * Returns courses saved to the user's wishlist
 */
export const getWishlistCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.WISHLIST);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'wishlist')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch wishlist courses' }
    };
  }
};

/**
 * Fetch Browse Courses
 * Returns all available courses for browsing
 */
export const getBrowseCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.BROWSE_COURSES);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'browse')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch browse courses' }
    };
  }
};

/**
 * Fetch Course Details
 * Returns detailed information about a specific course
 *
 * @param courseId - The ID of the course to fetch
 */
export const getCourseDetails = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseDetails;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourseDetails>>(
      ENDPOINTS.COURSE_DETAILS(courseId)
    );

    // response is the ApiResponse wrapper, response.data contains the BackendCourseDetails object
    const courseDetails = transformBackendCourseDetails(response.data);

    return { success: true, data: courseDetails, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch course details' }
    };
  }
};

/**
 * Save Course to Wishlist
 * Adds a course to the user's saved/wishlist
 *
 * @param courseId - The ID of the course to save
 */
export const saveCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    // POST returns: { success: true, data: null, message: "..." }
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.SAVE_COURSE, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to save course' }
    };
  }
};

/**
 * Remove Course from Wishlist
 * Removes a course from the user's saved/wishlist
 *
 * @param courseId - The ID of the course to unsave
 */
export const unsaveCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    // POST returns: { success: true, data: null, message: "..." }
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.UNSAVE_COURSE, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to unsave course' }
    };
  }
};

/**
 * Enroll in Course
 * Enrolls the user in a specific course
 *
 * @param courseId - The ID of the course to enroll in
 */
export const enrollInCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    // POST returns: { success: true, data: null, message: "..." }
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.ENROLL, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to enroll in course' }
    };
  }
};

/**
 * Mark Lesson as Complete
 * Marks a lesson as completed by the user
 *
 * @param lessonId - The ID of the lesson to mark complete
 * @param courseId - The ID of the course the lesson belongs to
 */
export const markLessonComplete = async (lessonId: number, courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    // POST returns: { success: true, data: null, message: "..." }
    await httpClient.post<ApiResponse<null>>(
      ENDPOINTS.MARK_LESSON_COMPLETE(courseId, lessonId),
      {}
    );
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to mark lesson complete' }
    };
  }
};
